import React from "react";
import {
  Icon,
  Link,
  SpaceBetween,
  TutorialPanelProps,
} from "@cloudscape-design/components";
import { Trans, useTranslation } from "react-i18next";
import { getDeviceBrowser } from "../../../utils/userAgentUtils";
import { ArrowUpIcon, PermissionIcon } from "../../icon";
import { useSkipTutorialStore } from "../../../hooks/useSkipTutorialStore";

export const useTutorial = (): TutorialPanelProps.Tutorial => {
  const { t } = useTranslation();
  const { setTutorialSkipped } = useSkipTutorialStore();

  const chromeTutorial = {
    title: null,
    description: null,
    completed: false,
    completedScreenDescription: null,
    tasks: [
      {
        title: null,
        steps: [
          {
            title: t("toolbar.tutorial.step1.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <SpaceBetween direction="vertical" size="s">
                  <Trans i18nKey={"toolbar.tutorial.step1.content"} />
                  <Link
                    variant="info"
                    onFollow={() => {
                      setTutorialSkipped(true);
                    }}
                  >
                    <Trans i18nKey={"toolbar.tutorial.step1.skipButton"} />
                  </Link>
                </SpaceBetween>
              </div>
            ),
            hotspotId: "hotspot1",
          },
          {
            title: t("toolbar.tutorial.step2.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step2.content"}>
                  {[<ArrowUpIcon />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot2",
          },
          {
            title: t("toolbar.tutorial.step3.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step3.content.chrome"}>
                  {[<PermissionIcon />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot3",
          },
          {
            title: t("toolbar.tutorial.step4.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step4.content"}>
                  {[<Icon name="settings" />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot4",
          },
        ],
      },
    ],
  };

  const edgeTutorial = {
    title: null,
    description: null,
    completed: false,
    completedScreenDescription: null,
    tasks: [
      {
        title: null,
        steps: [
          {
            title: t("toolbar.tutorial.step1.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <SpaceBetween direction="vertical" size="s">
                  <Trans i18nKey={"toolbar.tutorial.step1.content"} />
                  <Link
                    variant="info"
                    onFollow={() => {
                      setTutorialSkipped(true);
                    }}
                  >
                    <Trans i18nKey={"toolbar.tutorial.step1.skipButton"} />
                  </Link>
                </SpaceBetween>
              </div>
            ),
            hotspotId: "hotspot1",
          },
          {
            title: t("toolbar.tutorial.step2.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step2.content"}>
                  {[<ArrowUpIcon />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot2",
          },
          {
            title: t("toolbar.tutorial.step3.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step3.content.edge"}>
                  {[<PermissionIcon />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot3",
          },
          {
            title: t("toolbar.tutorial.step4.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step4.content"}>
                  {[<Icon name="settings" />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot4",
          },
        ],
      },
    ],
  };

  const safariTutorial = {
    title: null,
    description: null,
    completed: false,
    completedScreenDescription: null,
    tasks: [
      {
        title: null,
        steps: [
          {
            title: t("toolbar.tutorial.step1.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <SpaceBetween direction="vertical" size="s">
                  <Trans i18nKey={"toolbar.tutorial.step1.content"} />
                  <Link
                    variant="info"
                    onFollow={() => {
                      setTutorialSkipped(true);
                    }}
                  >
                    <Trans i18nKey={"toolbar.tutorial.step1.skipButton"} />
                  </Link>
                </SpaceBetween>
              </div>
            ),
            hotspotId: "hotspot1",
          },
          {
            title: t("toolbar.tutorial.step2.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step2.content"}>
                  {[<ArrowUpIcon />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot2",
          },
          {
            title: t("toolbar.tutorial.step3.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step3.content.safari"} />
              </div>
            ),
            hotspotId: "hotspot3",
          },
          {
            title: t("toolbar.tutorial.step4.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step4.content"}>
                  {[<Icon name="settings" />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot4",
          },
        ],
      },
    ],
  };

  const firefoxTutorial = {
    title: null,
    description: null,
    completed: false,
    completedScreenDescription: null,
    tasks: [
      {
        title: null,
        steps: [
          {
            title: t("toolbar.tutorial.step1.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <SpaceBetween direction="vertical" size="s">
                  <Trans i18nKey={"toolbar.tutorial.step1.content"} />
                  <Link
                    variant="info"
                    onFollow={() => {
                      setTutorialSkipped(true);
                    }}
                  >
                    <Trans i18nKey={"toolbar.tutorial.step1.skipButton"} />
                  </Link>
                </SpaceBetween>
              </div>
            ),
            hotspotId: "hotspot1",
          },
          {
            title: t("toolbar.tutorial.step2.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step2.content"}>
                  {[<ArrowUpIcon />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot2",
          },
          {
            title: t("toolbar.tutorial.step4.title"),
            content: (
              <div style={{ fontSize: "12px" }}>
                <Trans i18nKey={"toolbar.tutorial.step4.content"}>
                  {[<Icon name="settings" />]}
                </Trans>
              </div>
            ),
            hotspotId: "hotspot4",
          },
        ],
      },
    ],
  };

  const deviceBrowserType = getDeviceBrowser();
  switch (deviceBrowserType) {
    case "Chrome":
      return chromeTutorial;
    case "Edge":
      return edgeTutorial;
    case "Safari":
      return safariTutorial;
    case "Firefox":
      return firefoxTutorial;
    default:
      return chromeTutorial;
  }
};
